import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { currentUserPlannerToken } from "../../../utils/constants";
import { localStorageInstance } from "../../../utils/localStorage";
import { useRemoveMultipleParams } from "../../../hooks/useRemoveMultipleParams";
import { useTaskManager } from "../MyPlanner/useTaskManager";
import {
  selectFilteredCards,
  selectTodoTaskFilter,
} from "../../../redux/plannerFilterSlice";
import { getPlannerFilteredTasks } from "../utils/myTaskUtils";
import { getPlannerTokenSharing } from "../../../services/apiSystemSettings";

export function useMyPlannerPageState() {
  const [plannerToken, setPlannerToken] = useState(currentUserPlannerToken());
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const refreshToken = searchParams.get("refresh_token");
  const userId = searchParams.get("user_id");
  const { localTodos, handleDragEnd } = useTaskManager();
  const todoTaskFilter = useSelector(selectTodoTaskFilter);
  const filteredCards = useSelector(selectFilteredCards);
  const [filter, setFilter] = useState(todoTaskFilter || "All");
  const [removeSearchParams] = useRemoveMultipleParams();
  const domain =
    typeof window !== "undefined" ? window.location.hostname : "";
  const [plannerTokenSharingData, setPlannerTokenSharingData] = useState(null);
  const [isLoadingPlannerTokenSharing, setIsLoadingPlannerTokenSharing] =
    useState(false);

  useEffect(() => {
    if (!domain) {
      return;
    }

    setIsLoadingPlannerTokenSharing(true);
    getPlannerTokenSharing(domain)
      .then((response) => {
        setPlannerTokenSharingData(response?.data);

        if (response?.data?.data?.responseData?.enabled !== undefined) {
          localStorageInstance.setItem({
            key: "planner_token_sharing_enabled",
            value: response?.data?.data?.responseData?.enabled,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching planner token sharing:", error);
      })
      .finally(() => {
        setIsLoadingPlannerTokenSharing(false);
      });
  }, [domain]);

  useEffect(() => {
    if (!token) {
      return;
    }

    setPlannerToken(token);
    localStorageInstance.setItem({
      key: "planner_access_token",
      value: token,
    });
    localStorageInstance.setItem({
      key: "planner_refresh_token",
      value: refreshToken,
    });
    localStorageInstance.setItem({
      key: "planner_user_id",
      value: userId,
    });
    removeSearchParams(["token", "refresh_token", "user_id"]);
  }, [refreshToken, removeSearchParams, token, userId]);

  const filteredTasks = getPlannerFilteredTasks(localTodos, filter);

  const onTaskDragEnd = useCallback(
    (result) => {
      handleDragEnd(result, filteredTasks, filteredCards);
    },
    [filteredCards, filteredTasks, handleDragEnd]
  );

  return {
    filter,
    filteredTasks,
    isLoadingPlannerTokenSharing,
    plannerToken,
    plannerTokenSharingEnabled:
      plannerTokenSharingData?.data?.responseData?.enabled ?? false,
    setFilter,
    setPlannerToken,
    token,
    onTaskDragEnd,
  };
}
